import {
  buildStableColorIndexMap,
  seriesColorForKey,
  teamGroupColorKey,
} from "@/analytics/core/chart-palette";
import { METRIC_CATALOG } from "@/grid/config/catalog";
import {
  monthStartsInQuarter,
  monthStartsInYearThrough,
  nowToronto,
  toIsoDate,
} from "@/grid/core/periods";
import { indexMetricConfigs } from "@/grid/core/target-policy";
import type {
  DailyActivityRow,
  DepartmentDefinition,
  GridRawData,
  GridTeam,
  MetricDefinition,
  TeamPeriodRow,
} from "@/grid/core/types";
import type { TeamGroup } from "@/grid/data/assemble-grid";
import { assembleGrid, buildTeamGroups, getCell } from "@/grid/data/assemble-grid";
import {
  indexTargets,
  resolveAnnualTarget,
  resolveTargetForCell,
} from "@/grid/data/resolve-target";
import {
  DEFAULT_THRESHOLDS,
  getCellStatusForPeriod,
  getStatusColor,
  pacePercentage,
  resolveActual,
} from "@/grid/lib/pacing";
import { endOfMonth, format, parseISO, startOfMonth } from "date-fns";
import type {
  CompanyMonthMetric,
  CompanyMonthSnapshot,
  CompanyPaceSummary,
  CompanyViewModel,
  TeamContribution,
} from "@/company/core/types";

const SALES_METRIC = METRIC_CATALOG.sales;

function sumDailyField(
  repIds: number[],
  rows: DailyActivityRow[],
  from: string,
  to: string,
  field: keyof Pick<
    DailyActivityRow,
    "total_calls" | "connected_calls" | "meetings_held" | "sequences"
  >,
): number | null {
  let total = 0;
  let hasValue = false;

  for (const row of rows) {
    if (!repIds.includes(row.rep_id)) continue;
    if (row.activity_date < from || row.activity_date > to) continue;
    const value = row[field];
    if (value == null) continue;
    total += value;
    hasValue = true;
  }

  return hasValue ? total : null;
}

function sumPeriodFieldForTeams(
  rows: TeamPeriodRow[],
  teamIds: number[],
  periodType: string,
  periodStart: string,
  field: "sales_amount" | "opps_generated_count" | "opps_generated_value",
): number | null {
  let total = 0;
  let hasValue = false;

  for (const teamId of teamIds) {
    const row = rows.find(
      (entry) =>
        entry.team_id === teamId &&
        entry.period_type === periodType &&
        entry.period_start === periodStart,
    );
    const value = row?.[field];
    if (value == null) continue;
    total += value;
    hasValue = true;
  }

  return hasValue ? total : null;
}

function monthActivityRange(monthStart: string, asOf = nowToronto()): { from: string; to: string } {
  const currentMonthStart = toIsoDate(startOfMonth(asOf));
  const to =
    monthStart === currentMonthStart
      ? toIsoDate(asOf)
      : toIsoDate(endOfMonth(parseISO(monthStart)));
  return { from: monthStart, to };
}

function resolveCompanyMetricActual(
  metric: MetricDefinition,
  teamIds: number[],
  repIds: number[],
  raw: GridRawData,
  monthStart: string,
  asOf = nowToronto(),
): number {
  const { from, to } = monthActivityRange(monthStart, asOf);

  switch (metric.source.kind) {
    case "daily-sum":
      return resolveActual(
        sumDailyField(repIds, raw.dailyActivities, from, to, metric.source.field),
      );
    case "team-period":
      return resolveActual(
        sumPeriodFieldForTeams(
          raw.teamPeriodMetrics,
          teamIds,
          "month",
          monthStart,
          metric.source.field,
        ),
      );
    case "rep-period":
    case "rep-period-month-sum":
      return 0;
    default:
      return 0;
  }
}

function resolveCompanyMetricTargets(
  metric: MetricDefinition,
  groups: TeamGroup[],
  departmentId: string,
  targetIndex: ReturnType<typeof indexTargets>,
  configByMetric: ReturnType<typeof indexMetricConfigs>,
  monthStart: string,
  asOf = nowToronto(),
): { target: number | null; pacingTarget: number | null } {
  const currentMonthStart = toIsoDate(startOfMonth(asOf));
  const isCurrentMonth = monthStart === currentMonthStart;
  const resolveAsOf = isCurrentMonth ? asOf : endOfMonth(parseISO(monthStart));
  let targetTotal = 0;
  let pacingTotal = 0;
  let hasTarget = false;
  let hasPacing = false;

  for (const group of groups) {
    const scope = {
      departmentId,
      teamGroupKey: group.groupKey,
      teamId: group.id,
    };
    const resolved = resolveTargetForCell(
      metric,
      "mtd",
      scope,
      targetIndex,
      configByMetric,
      resolveAsOf,
    );
    if (resolved.target != null) {
      targetTotal += resolved.target;
      hasTarget = true;
    }
    const pacing = resolved.pacingTarget ?? resolved.target;
    if (pacing != null) {
      pacingTotal += pacing;
      hasPacing = true;
    }
  }

  return {
    target: hasTarget ? targetTotal : null,
    pacingTarget: hasPacing ? pacingTotal : null,
  };
}

function sumCompanyMetricActual(
  metric: MetricDefinition,
  groups: TeamGroup[],
  repsByTeam: Map<number, number[]>,
  raw: GridRawData,
  monthStart: string,
  asOf = nowToronto(),
): number {
  return groups.reduce((sum, group) => {
    const repIds = group.teamIds.flatMap((teamId) => repsByTeam.get(teamId) ?? []);
    return sum + resolveCompanyMetricActual(metric, group.teamIds, repIds, raw, monthStart, asOf);
  }, 0);
}

function buildCompanyMonthSnapshots(
  department: DepartmentDefinition,
  groups: TeamGroup[],
  repsByTeam: Map<number, number[]>,
  raw: GridRawData,
  monthStarts: string[],
  targetIndex: ReturnType<typeof indexTargets>,
  configByMetric: ReturnType<typeof indexMetricConfigs>,
  asOf = nowToronto(),
): CompanyMonthSnapshot[] {
  const currentMonthStart = toIsoDate(startOfMonth(asOf));

  return monthStarts.map((monthStart) => {
    const isCurrentMonth = monthStart === currentMonthStart;
    const metrics: CompanyMonthMetric[] = department.metrics.map((metric) => {
      const actual = sumCompanyMetricActual(metric, groups, repsByTeam, raw, monthStart, asOf);
      const { target, pacingTarget } = resolveCompanyMetricTargets(
        metric,
        groups,
        department.id,
        targetIndex,
        configByMetric,
        monthStart,
        asOf,
      );
      const compareTarget = isCurrentMonth ? (pacingTarget ?? target) : target;
      const statusAsOf = isCurrentMonth ? asOf : endOfMonth(parseISO(monthStart));

      return {
        metricId: metric.id,
        label: metric.label,
        shortLabel: metric.shortLabel ?? metric.label,
        actual,
        target: compareTarget,
        monthGoal: target,
        status: getCellStatusForPeriod(
          "mtd",
          actual,
          target,
          pacingTarget,
          department.thresholds,
          statusAsOf,
        ),
        format: metric.format,
      };
    });

    return {
      monthStart,
      label: format(parseISO(monthStart), "MMM"),
      isCurrentMonth,
      metrics,
    };
  });
}

function sumYtdActualForTeams(
  rows: TeamPeriodRow[],
  teamIds: number[],
  asOf = nowToronto(),
): number {
  return monthStartsInYearThrough(asOf).reduce((sum, monthStart) => {
    const value = sumPeriodFieldForTeams(rows, teamIds, "month", monthStart, "sales_amount");
    return sum + resolveActual(value);
  }, 0);
}

function buildPaceSummary(
  actual: number,
  compareTarget: number,
  fullTarget: number,
  label: string,
  targetSuffix: string,
  thresholds = DEFAULT_THRESHOLDS,
): CompanyPaceSummary {
  const percentage = pacePercentage(actual, compareTarget);

  return {
    actual,
    compareTarget,
    fullTarget,
    percentage,
    status: getStatusColor(actual, compareTarget, thresholds),
    label,
    targetSuffix,
  };
}

function buildTeamContribution(
  team: GridTeam,
  colorKey: string,
  color: string,
  qtdActual: number,
  qtdPaceTarget: number,
  ytdActual: number,
  ytdTarget: number,
  thresholds = DEFAULT_THRESHOLDS,
): TeamContribution {
  const qtdPacePct = pacePercentage(qtdActual, qtdPaceTarget);
  const ytdPct = pacePercentage(ytdActual, ytdTarget);

  return {
    teamId: team.id,
    teamName: team.name,
    colorKey,
    color,
    qtdActual,
    qtdPaceTarget,
    qtdPacePct,
    qtdSharePct: 0,
    ytdActual,
    ytdTarget,
    ytdPct,
    status: getStatusColor(qtdActual, qtdPaceTarget, thresholds),
  };
}

export function assembleCompanyView(
  department: DepartmentDefinition,
  raw: GridRawData,
  asOf = nowToronto(),
): CompanyViewModel {
  const grid = assembleGrid(department, raw, asOf);
  const thresholds = department.thresholds ?? DEFAULT_THRESHOLDS;
  const targetIndex = indexTargets(raw.targets);
  const configByMetric = indexMetricConfigs(
    department.id,
    department.metrics,
    raw.targetMetricConfigs,
  );

  const groups = buildTeamGroups(raw.teams, department);
  const repsByTeam = new Map<number, number[]>();
  for (const rep of raw.reps) {
    const list = repsByTeam.get(rep.team_id) ?? [];
    list.push(rep.id);
    repsByTeam.set(rep.team_id, list);
  }

  const colorMap = buildStableColorIndexMap(
    groups.map((group) => teamGroupColorKey(group.groupKey)),
  );

  let companyQtdActual = 0;
  let companyQtdPace = 0;
  let companyQtdFull = 0;
  let companyYtdActual = 0;
  let companyYtdTarget = 0;

  const teamRows: TeamContribution[] = [];

  for (const group of groups) {
    const team = grid.teams.find((row) => row.id === group.id);
    if (!team) continue;

    const scope = {
      departmentId: department.id,
      teamGroupKey: group.groupKey,
      teamId: group.id,
    };

    const qtdResolved = resolveTargetForCell(
      SALES_METRIC,
      "qtd",
      scope,
      targetIndex,
      configByMetric,
      asOf,
    );
    const qtdCell = getCell(team, "sales", "qtd");
    const qtdActual = resolveActual(qtdCell?.actual);
    const qtdPaceTarget = qtdResolved.pacingTarget ?? qtdResolved.target ?? 0;
    const qtdFullTarget = qtdResolved.target ?? qtdPaceTarget;

    const ytdActual = sumYtdActualForTeams(raw.teamPeriodMetrics, group.teamIds, asOf);
    const ytdTarget = resolveAnnualTarget(targetIndex, scope, SALES_METRIC, asOf) ?? 0;

    companyQtdActual += qtdActual;
    companyQtdPace += qtdPaceTarget;
    companyQtdFull += qtdFullTarget;
    companyYtdActual += ytdActual;
    companyYtdTarget += ytdTarget;

    teamRows.push(
      buildTeamContribution(
        team,
        teamGroupColorKey(group.groupKey),
        seriesColorForKey(teamGroupColorKey(group.groupKey), colorMap),
        qtdActual,
        qtdPaceTarget,
        ytdActual,
        ytdTarget,
        thresholds,
      ),
    );
  }

  for (const row of teamRows) {
    row.qtdSharePct =
      companyQtdActual > 0 ? Math.round((row.qtdActual / companyQtdActual) * 1000) / 10 : 0;
  }

  teamRows.sort((a, b) => b.qtdActual - a.qtdActual);

  const yearMonths = monthStartsInYearThrough(asOf);
  const quarterMonths = monthStartsInQuarter(asOf);
  const monthSnapshots = buildCompanyMonthSnapshots(
    department,
    groups,
    repsByTeam,
    raw,
    yearMonths,
    targetIndex,
    configByMetric,
    asOf,
  );

  return {
    departmentId: department.id,
    asOf: toIsoDate(asOf),
    fetchedAt: new Date().toISOString(),
    qtd: buildPaceSummary(
      companyQtdActual,
      companyQtdPace,
      companyQtdFull,
      "QTD Sales",
      " pace",
      thresholds,
    ),
    ytd: buildPaceSummary(
      companyYtdActual,
      companyYtdTarget,
      companyYtdTarget,
      "YTD Sales",
      "",
      thresholds,
    ),
    teams: teamRows,
    monthlySnapshots: monthSnapshots,
    quarterSnapshots: monthSnapshots.filter((snapshot) =>
      quarterMonths.includes(snapshot.monthStart),
    ),
  };
}
